import React, { useEffect, useRef } from 'react';
import { Assessment as AssessmentIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';

const TrainingCompetencyAssessmentCardFields = [
  {
    name: 'trainingMatrixNeedUpdated',
    label: 'Does the training matrix need to be updated?'
  },
  {
    name: 'changeImpactCompetencyAssessmentRequirements',
    label: 'Does the change impact competency assessment requirements?'
  },
  {
    name: 'changeRequireOperatorQualifications',
    label: 'Does the change require operator qualifications?'
  },
  {
    name: 'changeRequireOperatorOrTrainingCertification',
    label: 'Does the change require operator / training certification?'
  }
];

export const ChangeOrderTrainingAndCompetencyAssessmentCard: React.FC<
  BaseChangeOrderCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const { updatedTask, updateError: updateTaskError } = useUpdateTask(
    changeOrder.task.id || 0
  );
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    trainingMatrixNeedUpdated: changeOrder.trainingMatrixNeedUpdated || '',
    changeImpactCompetencyAssessmentRequirements:
      changeOrder.changeImpactCompetencyAssessmentRequirements || '',
    changeRequireOperatorQualifications:
      changeOrder.changeRequireOperatorQualifications || '',
    changeRequireOperatorOrTrainingCertification:
      changeOrder.changeRequireOperatorOrTrainingCertification || ''
  };

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <AssessmentIcon />
          Training and Competency Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {TrainingCompetencyAssessmentCardFields.map((field, key) => (
              <InlineSelect
                key={key}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                  { value: 'n/a', label: 'N/A' }
                ]}
              />
            ))}
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
